import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { Calendar, ChevronDown, ChevronRight, Download, Loader2 } from 'lucide-react';
import { shiftApi } from '@/services/api';
import type { ShiftPerformanceReport, POSShift } from '@/types';
import { formatMoney } from '@/utils/currency';
import { downloadCsv } from '@/utils/csv';

export default function ShiftPerformanceSection() {
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['shiftPerformance', fromDate, toDate],
    queryFn: async () => {
      const res = await shiftApi.getShiftPerformanceReport({ from_date: fromDate, to_date: toDate });
      return res.data.message as ShiftPerformanceReport;
    },
    enabled: Boolean(fromDate && toDate),
  });

  const exportRows = useMemo(() => {
    if (!data?.cashiers) return [];
    return data.cashiers.map((row) => ({
      Cashier: row.cashier_name,
      Shifts: row.shift_count,
      Sales: row.total_sales,
      Invoices: row.invoice_count,
      'Avg Ticket': row.avg_ticket_size,
      'Avg Shift Hours': row.avg_shift_hours,
      'Avg Variance': row.avg_variance,
      Returns: row.return_count,
      'Return Amount': row.return_amount,
      'Longest Shift (h)': row.longest_shift_hours,
      'Expired Open Shifts': row.expired_open_shifts,
    }));
  }, [data]);

  const handleExport = () => {
    if (!exportRows.length) return;
    downloadCsv(
      `shift-performance-${fromDate}-${toDate}.csv`,
      [
        { key: 'Cashier', label: 'Cashier' },
        { key: 'Shifts', label: 'Shifts' },
        { key: 'Sales', label: 'Sales' },
        { key: 'Invoices', label: 'Invoices' },
        { key: 'Avg Ticket', label: 'Avg Ticket' },
        { key: 'Avg Shift Hours', label: 'Avg Shift Hours' },
        { key: 'Avg Variance', label: 'Avg Variance' },
        { key: 'Returns', label: 'Returns' },
        { key: 'Return Amount', label: 'Return Amount' },
        { key: 'Longest Shift (h)', label: 'Longest Shift (h)' },
        { key: 'Expired Open Shifts', label: 'Expired Open Shifts' },
      ],
      exportRows,
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-5 h-5 text-gray-400" />
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border-gray-200 rounded-lg text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border-gray-200 rounded-lg text-sm" />
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={!exportRows.length}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading shift performance...
        </div>
      )}
      {isError && (
        <div className="text-red-600 text-sm py-4">
          {(error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to load report'}
        </div>
      )}

      {data && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-600">
            Shift limit: {data.max_shift_hours}h · {data.cashiers.length} cashier(s)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3" />
                  <th className="px-4 py-3">Cashier</th>
                  <th className="px-4 py-3 text-right">Shifts</th>
                  <th className="px-4 py-3 text-right">Sales</th>
                  <th className="px-4 py-3 text-right">Invoices</th>
                  <th className="px-4 py-3 text-right">Avg Ticket</th>
                  <th className="px-4 py-3 text-right">Avg Hours</th>
                  <th className="px-4 py-3 text-right">Variance</th>
                  <th className="px-4 py-3 text-right">Returns</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.cashiers.map((row) => (
                  <Fragment key={row.cashier}>
                    <tr className="hover:bg-gray-50/70">
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setExpanded(expanded === row.cashier ? null : row.cashier)} className="text-gray-500">
                          {expanded === row.cashier ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.cashier_name}</td>
                      <td className="px-4 py-3 text-right">{row.shift_count}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(row.total_sales)}</td>
                      <td className="px-4 py-3 text-right">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(row.avg_ticket_size)}</td>
                      <td className="px-4 py-3 text-right">{row.avg_shift_hours}h</td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(row.avg_variance)}</td>
                      <td className="px-4 py-3 text-right">{row.return_count} / {formatMoney(row.return_amount)}</td>
                    </tr>
                    {expanded === row.cashier && (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 bg-gray-50">
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-2 pr-4">Shift</th>
                                  <th className="text-left py-2 pr-4">Start</th>
                                  <th className="text-left py-2 pr-4">End</th>
                                  <th className="text-right py-2 pr-4">Hours</th>
                                  <th className="text-right py-2 pr-4">Sales</th>
                                  <th className="text-right py-2 pr-4">Invoices</th>
                                  <th className="text-right py-2">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.shifts.map((shift) => (
                                  <tr key={shift.name} className="border-t border-gray-200">
                                    <td className="py-2 pr-4 font-medium">{shift.name}</td>
                                    <td className="py-2 pr-4">{shift.shift_start ? new Date(shift.shift_start).toLocaleString() : '—'}</td>
                                    <td className="py-2 pr-4">{shift.shift_end ? new Date(shift.shift_end).toLocaleString() : '—'}</td>
                                    <td className="py-2 pr-4 text-right">{(shift as POSShift & { duration_hours?: number }).duration_hours ?? shift.hours_open ?? '—'}h</td>
                                    <td className="py-2 pr-4 text-right font-mono">{formatMoney(shift.total_sales || 0)}</td>
                                    <td className="py-2 pr-4 text-right">{shift.invoice_count || 0}</td>
                                    <td className="py-2 text-right">
                                      {shift.is_expired ? <span className="text-red-600 font-medium">Expired</span> : shift.status}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!data.cashiers.length && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">No shifts in this period.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
